import { useState, useCallback, useEffect } from 'react';
import { AxiosError } from 'axios'; // The client might throw AxiosError
import { createApiClient } from '../../api/poshwatch_client.ts';
import { DefaultApi, GenerateProductPhotoRequest, GetAvailableImageModels200Response } from 'poshwatch_ts_client';

interface UseImageGenParams {
  imageUrl?: string;
  base64Image?: string;
  modelId: string;
}

export const useImageGen = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<GetAvailableImageModels200Response['models']>({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);

  const fetchAvailableModels = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const client: DefaultApi = await createApiClient();
      const response = await client.getAvailableImageModels();

      if (response.data && response.data.success && response.data.models) {
        setAvailableModels(response.data.models);
        // Set initial category if available
        const categories = Object.keys(response.data.models);
        if (categories.length > 0) {
          setSelectedCategory(categories[0]);
          // Get styles for the first category, excluding the 'cover' property
          const styles = Object.keys(response.data.models[categories[0]]).filter(key => key !== 'cover');
          if (styles.length > 0) {
            setSelectedStyle(styles[0]);
          }
        }
      } else {
        setError('Failed to fetch available models: API returned success status but with invalid data.');
      }
    } catch (err) {
      console.error("Error fetching models:", err);
      let errorMessage = 'Failed to fetch model images. The Poshwatch client may need to be regenerated.';
      if (err instanceof AxiosError) {
        const apiError = err.response?.data as { error?: string; details?: string };
        errorMessage = apiError?.error || apiError?.details || err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  const generateImage = useCallback(async (params: UseImageGenParams) => {
    setIsLoading(true);
    setError(null);
    setGeneratedImageUrl(null);

    try {
      // Get the model URL from the selected model
      const [category, style, camera] = params.modelId.split('_');
      const selectedModel = availableModels?.[category]?.[style]?.[camera];

      if (!selectedModel?.url) {
        setError("Selected model URL not found. Please try selecting a different model.");
        setIsLoading(false);
        return;
      }

      const requestPayload: GenerateProductPhotoRequest = {
        modelId: params.modelId,
        modelUrl: selectedModel.url,
      };

      if (params.base64Image) {
        const base64Data = params.base64Image.startsWith('data:image')
          ? params.base64Image.split(',')[1]
          : params.base64Image;

        if (!base64Data) {
          setError("Invalid image format or empty image data.");
          setIsLoading(false);
          return;
        }
        requestPayload.base64Image = base64Data;
      } else if (params.imageUrl) {
        requestPayload.imageUrl = params.imageUrl;
      } else {
        setError("Either an image URL or a base64 image must be provided.");
        setIsLoading(false);
        return;
      }

      const client: DefaultApi = await createApiClient();
      const response = await client.generateProductPhoto(requestPayload);

      if (response.data && response.data.success && response.data.imageUrl) {
        setGeneratedImageUrl(response.data.imageUrl);
      } else {
        setError('Image generation API returned success status but with invalid data.');
      }
    } catch (err) {
      console.error("Error generating image:", err);
      let errorMessage = 'Failed to generate image.';
      if (err instanceof AxiosError) {
        const apiError = err.response?.data as { error?: string; details?: string };
        errorMessage = apiError?.error || apiError?.details || err.message;
        if (apiError?.details) {
          errorMessage += `: ${apiError.details}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [availableModels]);

  return {
    isLoading,
    error,
    generatedImageUrl,
    generateImage,
    setError,
    setGeneratedImageUrl,
    availableModels,
    selectedCategory,
    setSelectedCategory,
    selectedStyle,
    setSelectedStyle,
  };
};
